/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable require-await */
import React, {
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
} from 'react';
import { Table, Form } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { Button, FloatingActionButton } from 'appkit-react';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { EDIT_TABLE_COLUMNS_TYPE } from '@utils/enums';
import useTheme from '@hooks/useTheme';
import { EditableCell } from './components';
import deleteIcon from '@assets/imgs/cancel.png';
import deleteLightIcon from '@assets/imgs/cancel_light.png';
import _ from 'lodash';
import { handleEditTableData, returnPureData } from './dataProcessors';
import './index.scss';
import alert from '@components/alert';
// import { sortableContainer, sortableElement } from 'react-sortable-hoc';
// import arrayMove from 'array-move';

//===============

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Add this component before your main component
const DragHandle = ({ recordId }) => {
  const { attributes, listeners } = useSortable({ id: recordId });

  return (
    <div
      {...attributes}
      {...listeners}
      className="drag-handle-cell"
      style={{
        cursor: 'grab',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        className="appkiticon icon-menu-outline"
        style={{ fontSize: '16px' }}
      />
    </div>
  );
};

export const EditableContext = React.createContext(null);
/**
 * @name 可编辑表格组件
 * @author 吴迪 di.c.wu@pwc.com
 * @link https://4x.ant.design/components/table-cn/#API
 * @todo 暂时已知问题皆以解决，如有问题请联系作者或最后编辑的同学
 *
 * @desc 传入columns及dataSource即可轻松实现可编辑表格
 * @param tableProps 表格的属性（同antd-table）
 * 特殊说明：disabled+required 支持传入function，可以拿到当前行的数据
 * @param defaultColumns 默认的表头配置（同antd-table的表头配置）
 * @param defaultDataSource 默认表格数据
 * @param needDelete 是否需要删除icon，默认true
 * @param disabledActions 是否禁用所有的操作（如：新增、删除、编辑）
 * @param afterAddRowFunc 增加行之后进行的操作-比如默认值之类的.在这个方法里可以拿到新增的那一行数据进行处理
 * @param beforeEditRowFunc 编辑行成功之前进行的操作-比如清空值之类的.在这个方法里可以拿到新增的那一行数据进行处理
 * @param afterDeleteRowFunc 删除行之后的回调-这个方法可以拿到删除后的剩余行数据进行处理
 * @param checkIsSuccessfulDeleted 判断删除行是否成功
 * @param dataChange 增、删、改数据之后的回调
 * @param changeCustmizeData 自定义改数据
 * @param afterCancel cancel按钮后的回调
 * @param saveData 点击Generate保存数据之后的回调，可以拿到整个table的数据
 *
 * @version 0.1.1
 * @createdTime 2023-4-18
 * @lastModifiedTime 2023-4-28
 * @lastModifiedUser 吴迪
 */
const Index = (
  {
    edittingTableDataSource,
    setEdittingTableDataSource,
    tableProps = {},
    tableColumns = [],
    defaultDataSource = [],
    needDelete = true,
    disabledActions = false,
    afterAddRowFunc = (newItem) => {},
    afterDeleteRowFunc = (newItem) => {},
    checkIsSuccessfulDeleted = (newItem) => true,
    beforeEditRowFunc = (newItem) => {},
    dataChange = (newData) => {},
    saveData = (resData) => {},
    toggleEdit = () => {},
    changeCustmizeData = () => {},
    afterCancel = () => {},
  },
  editableRowRef,
) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  const [form] = Form.useForm();
  // 控制view模式与edit模式的,true：edit模式，false：view模式
  const [isEditMode, setIsEditMode] = useState(false);
  // 初始数据
  const initData = defaultDataSource.map((item) => {
    const newItem = { ...item };
    Object.keys(item).forEach((key) => {
      if (key === 'id') {
        newItem.id = item.id;
      } else {
        newItem[`${key}_${item.id}`] = item[key];
      }
    });
    return newItem;
  });
  const [tableDataSource, setTableDataSource] = useState(initData);
  const prevTableDataSource = useRef(initData);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before dragging starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = tableDataSource.findIndex((x) => x.id === active.id);
      const newIndex = tableDataSource.findIndex((x) => x.id === over.id);
      const newTableDataSource = handleArrayMove(
        tableDataSource,
        oldIndex,
        newIndex,
      );

      // Activity cannot be moved to first
      if (!newTableDataSource[0].isParent) {
        return;
      }

      changeData(newTableDataSource);
    }
  };

  useEffect(() => {
    prevTableDataSource.current = tableDataSource;
  }, [tableDataSource]);

  const getGanttStructure = (data) => {
    const mapOfParentIndexAndParentBlock = new Map();
    const allParentIndexArray = [];

    data.forEach((x, index) => {
      if (x.isParent) {
        allParentIndexArray.push(index);
      }
    });
    allParentIndexArray.forEach((e, i, a) => {
      if (i === a.length - 1) {
        mapOfParentIndexAndParentBlock.set(e, data.slice(e));
      } else {
        mapOfParentIndexAndParentBlock.set(e, data.slice(e, a[i + 1]));
      }
    });

    return {
      allParentIndexArray,
      mapOfParentIndexAndParentBlock,
    };
  };

  const isChildrenStatusChanged = (
    currentParentLineIndex,
    currentParentLine,
    mapOfParentIndexAndParentBlock,
    mapOfParentIndexAndParentBlockBeforeChange,
  ) => {
    let result = false;

    const parentLineBeforeChangeIndex = prevTableDataSource.current.findIndex(
      (d) => d.id === currentParentLine.id,
    );

    if (parentLineBeforeChangeIndex === -1) {
      return false;
    }

    const currentChildren = mapOfParentIndexAndParentBlock
      .get(currentParentLineIndex)
      ?.slice(1);

    const beforeChangeChildren = mapOfParentIndexAndParentBlockBeforeChange
      .get(parentLineBeforeChangeIndex)
      ?.slice(1);

    if (currentChildren.length !== beforeChangeChildren.length) {
      return true;
    }

    currentChildren.forEach((v, i) => {
      if (
        v.status !== beforeChangeChildren.find((x) => x.id === v.id)?.status
      ) {
        result = true;
      }
    });

    return result;
  };

  const setParentStatus = (newData) => {
    const { allParentIndexArray, mapOfParentIndexAndParentBlock } =
      getGanttStructure(newData);

    const {
      mapOfParentIndexAndParentBlock:
        mapOfParentIndexAndParentBlockBeforeChange,
    } = getGanttStructure(prevTableDataSource.current);

    allParentIndexArray.forEach((e, i, a) => {
      const currentParentLine = newData[e];
      const currentActivityLines = mapOfParentIndexAndParentBlock
        .get(e)
        .slice(1);
      if (
        isChildrenStatusChanged(
          e,
          currentParentLine,
          mapOfParentIndexAndParentBlock,
          mapOfParentIndexAndParentBlockBeforeChange,
        )
      ) {
        if (
          currentActivityLines.filter(
            (x) => x.status === 'On Track' || x.status === 'Completed',
          ).length
        ) {
          currentParentLine.status = 'On Track';
        }
        if (currentActivityLines.filter((x) => x.status === 'At Risk').length) {
          currentParentLine.status = 'At Risk';
        }
        if (currentActivityLines.filter((x) => x.status === 'Delayed').length) {
          currentParentLine.status = 'Delayed';
        }

        if (
          (currentActivityLines.filter((x) => x.status === 'Not Started')
            .length === currentActivityLines.length &&
            currentActivityLines.length !== 0) ||
          currentActivityLines.length === 0
        ) {
          currentParentLine.status = 'Not Started';
        }
        if (
          currentActivityLines.filter((x) => x.status === 'Completed')
            .length === currentActivityLines.length &&
          currentActivityLines.length !== 0
        ) {
          currentParentLine.status = 'Completed';
        }
      }
    });

    const resFormValue = {};
    newData.forEach((record) => {
      // 如果有的话，去把他的除了id和rowIndex的每一个key都循环处理
      if (record) {
        Object.keys(record).forEach((key) => {
          if (!['id', 'rowIndex'].includes(key)) {
            const dataIndex = `${key}_${record?.id}`;
            const currentKeyType = tableColumns.find(
              (col) => col.dataIndex === key,
            )?.type;
            // 判断是不是日期组件，如果是的话需要单独处理它的value
            const isDatePicker =
              currentKeyType === EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER;
            resFormValue[dataIndex] =
              isDatePicker && record[key] ? moment(record[key]) : record[key];
            resFormValue[key] =
              isDatePicker && record[key] ? moment(record[key]) : record[key];
          }
        });
      }
    });
    form.setFieldsValue(resFormValue);
    // x.status === 'On Track' ||
    // x.status === 'At Risk' ||
    // x.status === 'Completed' ||
    // x.status === 'Delayed',
  };

  const setParentActivityDate = (newData) => {
    const { allParentIndexArray, mapOfParentIndexAndParentBlock } =
      getGanttStructure(newData);

    allParentIndexArray.forEach((e, i, a) => {
      const currentParentLine = newData[e];
      const currentActivityLines = mapOfParentIndexAndParentBlock
        .get(e)
        .slice(1);
      if (
        currentParentLine.type === 'Activity' &&
        currentActivityLines.length !== 0
      ) {
        const startDateArray = [];
        const endDateArray = [];
        currentActivityLines.forEach((l) => {
          const currentEndDate = form.getFieldsValue()[`endDate_${l.id}`]?._d;
          const currentStartDate =
            form.getFieldsValue()[`startDate_${l.id}`]?._d;
          if (currentStartDate) {
            startDateArray.push(currentStartDate);
          }
          if (currentEndDate && l.type === 'Activity') {
            endDateArray.push(currentEndDate);
          }
          if (currentStartDate && l.type === 'Milestone') {
            endDateArray.push(currentStartDate);
          }
        });

        newData[e].startDate =
          startDateArray.length === 0
            ? newData[e].startDate
            : moment(Math.min(...startDateArray)).format('MM/DD/YYYY');
        newData[e].endDate =
          endDateArray.length === 0
            ? newData[e].endDate
            : moment(Math.max(...endDateArray)).format('MM/DD/YYYY');
      }
    });
  };

  // 增删的时候通过该方法改数据，改当前组件使用的data以及暴露给父组件的data
  const changeData = (data) => {
    const newData = returnPureData(data);
    dataChange(newData);

    setParentActivityDate(newData);

    setParentStatus(newData);
    // 这里使用者可能需要改这个data，所以改完之后我再进行一次数据处理
    setTableDataSource(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newData,
      }),
    );
    setEdittingTableDataSource(newData);
  };

  const handleAdd = async (isParent, parentId) => {
    const newData = {
      id: uuidv4(),
      isParent,
      type: 'Activity',
      status: 'Not Started',
    };
    let newTableData = [];
    let hasNextParent = false;
    let nextParentIndex;

    if (parentId) {
      const currentParentIndex = tableDataSource.findIndex(
        (d) => d.id === parentId,
      );

      tableDataSource.every((d, i) => {
        if (i > currentParentIndex && d.isParent) {
          hasNextParent = true;
          nextParentIndex = i;
          return false;
        }
        return true;
      });
    }

    if (parentId && hasNextParent) {
      const leftArray = tableDataSource.slice(0, nextParentIndex);
      const rightArray = tableDataSource.slice(nextParentIndex);
      newTableData = [...leftArray, newData, ...rightArray];
    } else {
      newTableData = [...tableDataSource, newData];
    }

    afterAddRowFunc(newData);
    changeData(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newTableData,
      }),
    );
  };

  useEffect(() => {
    if (tableDataSource.length === 0 && isEditMode) {
      handleAdd(true);
    }
  }, [tableDataSource, isEditMode]);

  // 删除
  const handleDelete = async (id) => {
    if (checkIsSuccessfulDeleted(id)) {
      const newData = tableDataSource.filter((item) => item.id !== id);
      afterDeleteRowFunc(newData);
      changeData(
        handleEditTableData({
          formVal: form.getFieldsValue(),
          tableData: newData,
        }),
      );
    }
  };

  const handleDeleteParent = async (parentId) => {
    const parentIndex = tableDataSource.findIndex(
      (item) => item.id === parentId,
    );

    const left = tableDataSource.slice(0, parentIndex);
    const other = tableDataSource.slice(parentIndex);
    let right = [];
    const nextParentIndex = other.findIndex(
      (item) => item.id !== parentId && item.isParent,
    );
    if (nextParentIndex !== -1) {
      right = other.slice(nextParentIndex);
    }

    const newData = [...left, ...right];
    afterDeleteRowFunc(newData);
    changeData(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newData,
      }),
    );
  };

  // 保存数据 - 失焦即调
  const handleSave = useCallback(
    async (row, needRefreshTableData) => {
      const newData = [...tableDataSource];
      const index = newData.findIndex((item) => row.id === item.id);
      const curItem = newData[index];
      // console.log('保存curItem：', curItem);
      if (_.isEqual(row, curItem)) {
        return console.log('没有变化');
      }
      newData.splice(index, 1, {
        ...curItem,
        ...row,
      });
      const resData = handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newData,
      });
      // 在这里拿到处理过的数据之后这个数据才是正确的，所以在这里调before
      // 否则使用的地方拿到的数据是有问题的
      beforeEditRowFunc(resData[index]);
      // changeData(resData);

      if (needRefreshTableData) {
        changeData(resData);
      } else {
        // START - 处理使用组件的地方改变数据的逻辑
        const newPureData = returnPureData(resData);
        dataChange(newPureData);
        // 最终要设置的form的值
        const resFormValue = {};
        newPureData.forEach((record) => {
          // 如果有的话，去把他的除了id和rowIndex的每一个key都循环处理
          if (record) {
            Object.keys(record).forEach((key) => {
              if (!['id', 'rowIndex'].includes(key)) {
                const dataIndex = `${key}_${record?.id}`;
                const currentKeyType = tableColumns.find(
                  (col) => col.dataIndex === key,
                )?.type;
                // 判断是不是日期组件，如果是的话需要单独处理它的value
                const isDatePicker =
                  currentKeyType === EDIT_TABLE_COLUMNS_TYPE.DATE_PICKER;
                resFormValue[dataIndex] =
                  isDatePicker && record[key]
                    ? moment(record[key])
                    : record[key];
                resFormValue[key] =
                  isDatePicker && record[key]
                    ? moment(record[key])
                    : record[key];
              }
            });
          }
        });
        form.setFieldsValue(resFormValue);
        // END - 处理使用组件的地方改变数据的逻辑
      }
    },
    [tableDataSource],
  );

  // 获取禁用的类名 - 如果不是禁用的就返回空字符串
  const getDisabledClassName = useMemo(
    () => (disabledActions ? 'disabled_style' : ''),
    [disabledActions],
  );

  useEffect(() => {
    toggleEdit(isEditMode);
  }, [isEditMode, toggleEdit]);

  // 判断是否可操作
  const actionWrapper = useCallback(
    (action) => {
      if (disabledActions) {
        return;
      }
      action && action();
    },
    [disabledActions],
  );

  // 处理表头数据-追加 序号列/删除列 且处理可编辑逻辑
  const columns = useMemo(() => {
    const newColumns = [...tableColumns];

    // Add delete column if needed
    if (needDelete && isEditMode) {
      newColumns.push({
        title: '',
        dataIndex: 'operation',
        width: 40,
        render: (_, record) =>
          tableDataSource.length >= 1 ? (
            <div className={`${getDisabledClassName} delete_wrapper`}>
              <img
                onClick={() => {
                  const deletedRow = edittingTableDataSource.find(
                    (x) => x.id === record.id,
                  );
                  const deletedRowIndex = edittingTableDataSource.findIndex(
                    (x) => x.id === record.id,
                  );
                  if (
                    deletedRow.isParent &&
                    deletedRowIndex !== edittingTableDataSource.length - 1 &&
                    !edittingTableDataSource[deletedRowIndex + 1].isParent
                  ) {
                    alert.confirm({
                      onOk: async () => handleDeleteParent(record.id),
                      onCancel: async () => {},
                      okText: 'yes',
                      content:
                        'Do you want to delele this Parent with Activities?',
                      noCloseIcon: true,
                      async: true,
                      maskClosable: false,
                    });
                  } else {
                    actionWrapper(() => {
                      handleDelete(record.id);
                    });
                  }
                }}
                src={isLightMode ? deleteLightIcon : deleteIcon}
                className={`${getDisabledClassName} delete_icon`}
              />
            </div>
          ) : null,
      });
    }

    return newColumns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          type: col.type,
          compProps: col.compProps,
          required: col.required,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          alwaysEditMode: isEditMode,
          placeholder: col.placeholder,
          disabled: disabledActions,
          handleSave,
          handleAdd,
          setParentActivityDate,
          isEditMode,
        }),
      };
    });
  }, [isEditMode, tableColumns, needDelete, disabledActions, tableDataSource]);

  // const SortableItem = sortableElement((props) => (
  //   <tr className="ant-table-row-tr" {...props} />
  // ));
  // const SortableContainer = sortableContainer((props) => <tbody {...props} />);

  const modalTableClassUUID = uuidv4();
  // const getSortableContainer = () =>
  //   document.querySelector(`.modal-table${modalTableClassUUID}`);
  // const sortableContainerDefaultProps = {
  //   distance: 20,
  //   axis: 'y',
  //   lockAxis: 'y',
  //   useWindowAsScrollContainer: true,
  //   useDragHandle: true,
  //   helperContainer: getSortableContainer,
  // };

  const changeMapToArrayOfParentBlock = (
    newAllParentIndexArray,
    mapOfParentIndexAndParentBlock,
  ) => {
    let result = [];
    newAllParentIndexArray.forEach((x) => {
      result = [...result, ...mapOfParentIndexAndParentBlock.get(x)];
    });
    return result;
  };

  const handleArrayMove = (tableDataSource, oldIndex, newIndex) => {
    let newTableDataSource = [];
    const isTargetParent = tableDataSource[oldIndex].isParent;

    if (isTargetParent && newIndex !== oldIndex) {
      const mapOfParentIndexAndParentBlock = new Map();
      const allParentIndexArray = [];

      tableDataSource.forEach((x, index) => {
        if (x.isParent) {
          allParentIndexArray.push(index);
        }
      });
      allParentIndexArray.forEach((e, i, a) => {
        if (i === a.length - 1) {
          mapOfParentIndexAndParentBlock.set(e, tableDataSource.slice(e));
        } else {
          mapOfParentIndexAndParentBlock.set(
            e,
            tableDataSource.slice(e, a[i + 1]),
          );
        }
      });
      let newAllParentIndexArray;
      let from;
      let to;
      if (newIndex > oldIndex) {
        from = allParentIndexArray.indexOf(oldIndex);
        to =
          allParentIndexArray.length -
          1 -
          allParentIndexArray.toReversed().findIndex((x) => newIndex >= x);
        newAllParentIndexArray = arrayMove(allParentIndexArray, from, to);
      } else {
        from = allParentIndexArray.indexOf(oldIndex);
        to = allParentIndexArray.findIndex((x) => x >= newIndex);
        newAllParentIndexArray = arrayMove(allParentIndexArray, from, to);
      }

      newTableDataSource = changeMapToArrayOfParentBlock(
        newAllParentIndexArray,
        mapOfParentIndexAndParentBlock,
      );
    } else {
      // move child
      newTableDataSource = arrayMove(tableDataSource, oldIndex, newIndex);
    }

    return newTableDataSource;
  };

  // const onSortEnd = (props) => {
  //   const { oldIndex, newIndex } = props;
  //   const newTableDataSource = handleArrayMove(
  //     tableDataSource,
  //     oldIndex,
  //     newIndex,
  //   );

  //   // Activity cannot be moved to first
  //   if (!newTableDataSource[0].isParent) {
  //     return;
  //   }

  //   changeData(newTableDataSource);
  // };

  const DraggableBodyRow = ({ children, ...props }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: props['data-row-key'],
      disabled: !isEditMode,
    });

    const style = useMemo(
      () => ({
        ...props.style,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }),
      [props.style, transform, transition, isDragging],
    );

    // Pass drag props to children so OrgCombCell can use them
    const childrenWithDragProps = React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          dragAttributes: attributes,
          dragListeners: listeners,
        });
      }
      return child;
    });

    return (
      <tr ref={setNodeRef} style={style} {...props}>
        {childrenWithDragProps}
      </tr>
    );
  };

  // const CombinedForm = (props) => (
  //   <SortableContainer
  //     {...sortableContainerDefaultProps}
  //     {...props}
  //     onSortEnd={onSortEnd}
  //   />
  // );

  const CombinedEditableCell = (props) => (
    <EditableCell
      changeData={changeData}
      {...props}
      tableDataSource={tableDataSource}
    />
  );

  const components = {
    body: {
      wrapper: ({ children }) => (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
        >
          <SortableContext
            items={tableDataSource.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <tbody>{children}</tbody>
          </SortableContext>
        </DndContext>
      ),
      row: DraggableBodyRow,
      cell: CombinedEditableCell,
    },
  };

  const renderBtnGroups = () =>
    isEditMode ? (
      <div className="btn-group">
        <FloatingActionButton
          className="cancel-btn"
          style={{ marginRight: 6 }}
          onClick={() => {
            // 恢复没编辑之前的值
            setTableDataSource(initData);
            setEdittingTableDataSource(initData);
            // console.log('defaultDataSource: ', defaultDataSource, initData);
            // 恢复view模式
            setIsEditMode(false);
            afterCancel();
          }}
        >
          Cancel
        </FloatingActionButton>
        <FloatingActionButton
          className="generate-btn"
          style={{ height: 24 }}
          onClick={async () => {
            await form?.validateFields();
            const newArr = handleEditTableData({
              formVal: form.getFieldsValue(),
              tableData: tableDataSource,
            });
            // 在这里把数据暴露出去方便各自调接口
            changeCustmizeData(newArr);
            saveData(returnPureData(newArr));
            changeData(newArr);
            setIsEditMode(false);
          }}
        >
          Generate
        </FloatingActionButton>
      </div>
    ) : (
      <Button
        className="btn role-control edit-btn"
        style={{ height: 26 }}
        onClick={() => setIsEditMode(true)}
      >
        Edit
      </Button>
    );

  return (
    <EditableContext.Provider value={form}>
      <div
        className={`pwc_gantt_editor_table_wrapper ${
          isEditMode ? 'edit_mode' : null
        }`}
      >
        <div className="pwc_editor_table_header">
          <span>Table</span>
          {renderBtnGroups()}
        </div>
        <Form
          ref={editableRowRef}
          form={form}
          component={false}
          autoComplete="off"
        >
          <Table
            components={components}
            className={`modal-table${modalTableClassUUID}`}
            rowKey="id"
            bordered
            dataSource={tableDataSource}
            columns={columns}
            locale={{
              emptyText: () => (
                <div
                  className="edit-table-empty"
                  style={{ height: 200, paddingTop: 42 }}
                >
                  <InboxOutlined style={{ fontSize: 50 }} />
                  <p>
                    Select &quot;Edit Visual Data&quot; button to get started
                  </p>
                </div>
              ),
            }}
            {...tableProps}
          />
        </Form>
        {/* 目前快速点击add new 是有问题的，包括export之类的数据都会有些问题 */}
      </div>
    </EditableContext.Provider>
  );
};

export default forwardRef(Index);
